import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Single-flight guard for async handlers.
 *
 * Pass an async function to `run` and the hook returns a wrapped invocation
 * that respects whatever an earlier call is still doing:
 *
 *   - mode: 'ignore' (default, for user-initiated submits)
 *       If a previous call is still in flight, the new invocation is a no-op
 *       and returns `undefined`. The visible spinner/disabled button is what
 *       tells the user their click registered, so we never silently swallow
 *       state — the UI gate is always paired with this code gate.
 *
 *   - mode: 'abortReplace' (for internally-triggered fetches, e.g. background
 *       reloads keyed on route state). The previous AbortController is
 *       aborted and the new call starts. The async function receives an
 *       AbortSignal it must forward to axios/fetch via `{ signal }`.
 *
 * `pending` is a boolean you can wire to `disabled` on the submit button and
 * to `aria-busy` on the form. It flips to `false` on settle (success OR
 * error) so error states re-enable the button immediately.
 *
 * The hook is also unmount-safe: a state update that races a component
 * unmount is dropped.
 */
export type InflightMode = 'ignore' | 'abortReplace';

export type InflightRunner<TArgs extends unknown[], TResult> = (
    ...args: TArgs
) => Promise<TResult | undefined>;

export function useInflight<TArgs extends unknown[], TResult>(
    fn: (signal: AbortSignal, ...args: TArgs) => Promise<TResult>,
    mode: InflightMode = 'ignore'
): { pending: boolean; run: InflightRunner<TArgs, TResult> } {
    const [pending, setPending] = useState(false);
    const inflightRef = useRef<AbortController | null>(null);
    const mountedRef = useRef(true);
    // Captured in a ref so callers don't need to memoize `fn` themselves.
    // The wrapped `run` identity stays stable across renders.
    const fnRef = useRef(fn);
    fnRef.current = fn;

    useEffect(
        () => () => {
            mountedRef.current = false;
            inflightRef.current?.abort();
            inflightRef.current = null;
        },
        []
    );

    const run = useCallback<InflightRunner<TArgs, TResult>>(
        async (...args) => {
            if (inflightRef.current) {
                if (mode === 'ignore') {
                    return undefined;
                }
                // abortReplace
                inflightRef.current.abort();
            }
            const ctrl = new AbortController();
            inflightRef.current = ctrl;
            if (mountedRef.current) setPending(true);
            try {
                return await fnRef.current(ctrl.signal, ...args);
            } finally {
                // Only clear the slot if THIS invocation is still the active
                // one (abortReplace may have moved on to a newer controller).
                if (inflightRef.current === ctrl) {
                    inflightRef.current = null;
                    if (mountedRef.current) setPending(false);
                }
            }
        },
        [mode]
    );

    return { pending, run };
}
